import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import confetti from 'canvas-confetti';
import PDFViewer from "../components/PDFViewer";
import ResumeEditForm from "../components/ResumeEditForm";
import ResumeMetrics from "../components/ResumeMetrics";
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { generatePDF } from "../components/generatePDF";
import PopupPortal from "../components/PopupPortal";
import useNavigationWarning from '../hooks/useNavigationWarning';
import '../styles/ResultsPage.css';
import csrfService from '../services/csrfService';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pdfData, setPdfData] = useState(null);
  const [fields, setFields] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [pdfFilename, setPdfFilename] = useState('formatted_resume.pdf');
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [showPopup, setShowPopup] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Warn users before leaving while editing - only warn if they're in edit mode and haven't downloaded
  useNavigationWarning(true, 'Make sure you have downloaded your resume before leaving this page. You may not be able to navigate back.');

  const triggerConfetti = () => {
    // Use the default global confetti function
    confetti({
      // zIndex: 9999,
      particleCount: 200,
      spread: 140,
      origin: { y: 0.3 },
      colors: ['#16a34a', '#4ade80', '#22c55e', '#15803d', '#166534']
    });

    // Fire more confetti from the sides

    confetti({
      // zIndex: 9999,
      particleCount: 100,
      angle: 60,
      spread: 110,
      origin: { x: 0 },
      colors: ['#16a34a', '#4ade80', '#22c55e', '#15803d', '#166534']
    });

    confetti({
      // zIndex: 9999,
      particleCount: 100,
      angle: 120,
      spread: 110,
      origin: { x: 1 },
      colors: ['#16a34a', '#4ade80', '#22c55e', '#15803d', '#166534']
    });

  };

  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const stripeServerUrl = isDevelopment
    ? 'http://localhost:4000/node'
    : (process.env.REACT_APP_STRIPE_SERVER_URL || 'https://your-aws-stripe-server-endpoint');
  // Add this effect to trigger confetti when popup is shown
  useEffect(() => {
    if (showPopup && !isLoading) {
      // Delay the confetti slightly to ensure popup is visible
      triggerConfetti();
    }
  }, [showPopup, isLoading]);

  useEffect(() => {
    // Add a style tag directly to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .app-container {
        background: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-image: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-attachment: fixed !important;
      }
      
      .app-container::before,
      .app-container::after {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);



  // Function to toggle education visibility
  const handleToggleEducation = (eduIndex) => {
    const updatedFields = { ...fields };
    if (!updatedFields.education[eduIndex].omitted) {
      updatedFields.education[eduIndex].omitted = true;
    } else {
      updatedFields.education[eduIndex].omitted = false;
    }

    setFields(updatedFields);
  };

  // Function to delete an education entry
  const handleDeleteEducation = (eduIndex) => {
    if (window.confirm("Are you sure you want to delete this education entry?")) {
      const updatedFields = { ...fields };
      updatedFields.education.splice(eduIndex, 1);

      setFields(updatedFields);
    }
  };
  // Function to add a new company
  const handleAddCompany = () => {
    const updatedFields = { ...fields };
    if (!updatedFields.companies) {
      updatedFields.companies = [];
    }

    updatedFields.companies.push({
      name: '',
      location: '',
      roles: [{
        title: '',
        start_year: '',
        end_year: '',
        bullets: ['']
      }]
    });

    // Auto-expand the new company
    const newCompanyIndex = updatedFields.companies.length - 1;
    setExpandedCompanies({
      ...expandedCompanies,
      [newCompanyIndex]: true
    });

    // Auto-expand the first role of the new company
    setExpandedRoles({
      ...expandedRoles,
      [`${newCompanyIndex}-0`]: true
    });

    setFields(updatedFields);

  };

  // Function to toggle company visibility
  const handleToggleCompany = (companyIndex) => {
    const updatedFields = { ...fields };
    if (!updatedFields.companies[companyIndex].omitted) {
      updatedFields.companies[companyIndex].omitted = true;
    } else {
      updatedFields.companies[companyIndex].omitted = false;
    }

    setFields(updatedFields);

  };

  // Function to delete a company
  const handleDeleteCompany = (companyIndex) => {
    if (window.confirm(`Are you sure you want to delete ${fields.companies[companyIndex].name || 'this company'}?`)) {
      const updatedFields = { ...fields };
      updatedFields.companies.splice(companyIndex, 1);

      // Clean up expandedCompanies and expandedRoles state
      const newExpandedCompanies = { ...expandedCompanies };
      const newExpandedRoles = { ...expandedRoles };

      Object.keys(newExpandedCompanies).forEach(idx => {
        const index = Number(idx);
        if (index === companyIndex) {
          delete newExpandedCompanies[idx];
        } else if (index > companyIndex) {
          newExpandedCompanies[index - 1] = newExpandedCompanies[idx];
          delete newExpandedCompanies[idx];
        }
      });

      Object.keys(newExpandedRoles).forEach(key => {
        const [compIdx, roleIdx] = key.split('-').map(Number);
        if (compIdx === companyIndex) {
          delete newExpandedRoles[key];
        } else if (compIdx > companyIndex) {
          newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = newExpandedRoles[key];
          delete newExpandedRoles[key];
        }
      });

      setExpandedCompanies(newExpandedCompanies);
      setExpandedRoles(newExpandedRoles);
      setFields(updatedFields);

    }
  };

  // Function to move a company up
  const handleMoveCompanyUp = (companyIndex) => {
    if (companyIndex === 0) return; // Can't move up if already at the top

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex];
    updatedFields.companies[companyIndex] = updatedFields.companies[companyIndex - 1];
    updatedFields.companies[companyIndex - 1] = temp;

    // Update expanded states
    const newExpandedCompanies = {};
    Object.keys(expandedCompanies).forEach(idx => {
      const index = Number(idx);
      if (index === companyIndex) {
        newExpandedCompanies[index - 1] = expandedCompanies[idx];
      } else if (index === companyIndex - 1) {
        newExpandedCompanies[index + 1] = expandedCompanies[idx];
      } else {
        newExpandedCompanies[index] = expandedCompanies[idx];
      }
    });

    const newExpandedRoles = {};
    Object.keys(expandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex) {
        newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = expandedRoles[key];
      } else if (compIdx === companyIndex - 1) {
        newExpandedRoles[`${compIdx + 1}-${roleIdx}`] = expandedRoles[key];
      } else {
        newExpandedRoles[key] = expandedRoles[key];
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedRoles(newExpandedRoles);
    setFields(updatedFields);

  };

  // Function to move a company down
  const handleMoveCompanyDown = (companyIndex) => {
    if (companyIndex === fields.companies.length - 1) return; // Can't move down if already at the bottom

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex];
    updatedFields.companies[companyIndex] = updatedFields.companies[companyIndex + 1];
    updatedFields.companies[companyIndex + 1] = temp;

    // Update expanded states
    const newExpandedCompanies = {};
    Object.keys(expandedCompanies).forEach(idx => {
      const index = Number(idx);
      if (index === companyIndex) {
        newExpandedCompanies[index + 1] = expandedCompanies[idx];
      } else if (index === companyIndex + 1) {
        newExpandedCompanies[index - 1] = expandedCompanies[idx];
      } else {
        newExpandedCompanies[index] = expandedCompanies[idx];
      }
    });

    const newExpandedRoles = {};
    Object.keys(expandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex) {
        newExpandedRoles[`${compIdx + 1}-${roleIdx}`] = expandedRoles[key];
      } else if (compIdx === companyIndex + 1) {
        newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = expandedRoles[key];
      } else {
        newExpandedRoles[key] = expandedRoles[key];
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedRoles(newExpandedRoles);
    setFields(updatedFields);

  };

  // Function to move a bullet up within a role
  const handleMoveBulletUp = (companyIndex, roleIndex, bulletIndex) => {
    if (bulletIndex === 0) return; // Can't move up if already at the top

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex];
    updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex - 1];
    updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex - 1] = temp;

    setFields(updatedFields);
  };

  // Function to move a bullet down within a role
  const handleMoveBulletDown = (companyIndex, roleIndex, bulletIndex) => {
    const bullets = fields.companies[companyIndex].roles[roleIndex].bullets;
    if (bulletIndex === bullets.length - 1) return; // Can't move down if already at the bottom

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex];
    updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex + 1];
    updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex + 1] = temp;

    setFields(updatedFields);
  };

  // Function to add a role to a company
  const handleAddRole = (companyIndex) => {
    const updatedFields = { ...fields };

    if (!updatedFields.companies[companyIndex].roles) {
      updatedFields.companies[companyIndex].roles = [];
    }

    updatedFields.companies[companyIndex].roles.push({
      title: '',
      start_year: '',
      end_year: '',
      bullets: ['']
    });

    // Auto-expand the new role
    const newRoleIndex = updatedFields.companies[companyIndex].roles.length - 1;
    const roleKey = `${companyIndex}-${newRoleIndex}`;
    setExpandedRoles({
      ...expandedRoles,
      [roleKey]: true
    });

    setFields(updatedFields);

  };

  // Function to toggle role visibility
  const handleToggleRole = (companyIndex, roleIndex) => {
    const updatedFields = { ...fields };
    if (!updatedFields.companies[companyIndex].roles[roleIndex].omitted) {
      updatedFields.companies[companyIndex].roles[roleIndex].omitted = true;
    } else {
      updatedFields.companies[companyIndex].roles[roleIndex].omitted = false;
    }

    setFields(updatedFields);

  };

  // Function to delete a role
  const handleDeleteRole = (companyIndex, roleIndex) => {
    if (window.confirm(`Are you sure you want to delete ${fields.companies[companyIndex].roles[roleIndex].title || 'this role'}?`)) {
      const updatedFields = { ...fields };
      updatedFields.companies[companyIndex].roles.splice(roleIndex, 1);

      // Clean up expandedRoles state
      const newExpandedRoles = { ...expandedRoles };
      Object.keys(newExpandedRoles).forEach(key => {
        const [compIdx, roleIdx] = key.split('-').map(Number);
        if (compIdx === companyIndex && roleIdx === roleIndex) {
          delete newExpandedRoles[key];
        } else if (compIdx === companyIndex && roleIdx > roleIndex) {
          newExpandedRoles[`${compIdx}-${roleIdx - 1}`] = newExpandedRoles[key];
          delete newExpandedRoles[key];
        }
      });

      setExpandedRoles(newExpandedRoles);
      setFields(updatedFields);

    }
  };

  // Function to move a role up
  const handleMoveRoleUp = (companyIndex, roleIndex) => {
    if (roleIndex === 0) return; // Can't move up if already at the top

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex].roles[roleIndex];
    updatedFields.companies[companyIndex].roles[roleIndex] = updatedFields.companies[companyIndex].roles[roleIndex - 1];
    updatedFields.companies[companyIndex].roles[roleIndex - 1] = temp;

    // Update expanded roles state
    const newExpandedRoles = { ...expandedRoles };
    const currentKey = `${companyIndex}-${roleIndex}`;
    const previousKey = `${companyIndex}-${roleIndex - 1}`;

    const currentExpanded = expandedRoles[currentKey];
    const previousExpanded = expandedRoles[previousKey];

    if (currentExpanded !== undefined) {
      newExpandedRoles[previousKey] = currentExpanded;
      delete newExpandedRoles[currentKey];
    }

    if (previousExpanded !== undefined) {
      newExpandedRoles[currentKey] = previousExpanded;
      delete newExpandedRoles[previousKey];
    }

    setExpandedRoles(newExpandedRoles);
    setFields(updatedFields);

  };

  // Function to move a role down
  const handleMoveRoleDown = (companyIndex, roleIndex) => {
    if (roleIndex === fields.companies[companyIndex].roles.length - 1) return; // Can't move down if already at the bottom

    const updatedFields = { ...fields };
    const temp = updatedFields.companies[companyIndex].roles[roleIndex];
    updatedFields.companies[companyIndex].roles[roleIndex] = updatedFields.companies[companyIndex].roles[roleIndex + 1];
    updatedFields.companies[companyIndex].roles[roleIndex + 1] = temp;

    // Update expanded roles state
    const newExpandedRoles = { ...expandedRoles };
    const currentKey = `${companyIndex}-${roleIndex}`;
    const nextKey = `${companyIndex}-${roleIndex + 1}`;

    const currentExpanded = expandedRoles[currentKey];
    const nextExpanded = expandedRoles[nextKey];

    if (currentExpanded !== undefined) {
      newExpandedRoles[nextKey] = currentExpanded;
      delete newExpandedRoles[currentKey];
    }

    if (nextExpanded !== undefined) {
      newExpandedRoles[currentKey] = nextExpanded;
      delete newExpandedRoles[nextKey];
    }

    setExpandedRoles(newExpandedRoles);
    setFields(updatedFields);

  };

  // Function to add a bullet to a role
  const handleAddBullet = (companyIndex, roleIndex) => {
    const updatedFields = { ...fields };

    if (!updatedFields.companies[companyIndex].roles[roleIndex].bullets) {
      updatedFields.companies[companyIndex].roles[roleIndex].bullets = [];
    }

    updatedFields.companies[companyIndex].roles[roleIndex].bullets.push('');

    setFields(updatedFields);

  };

  const handleToggleBullet = (companyIndex, roleIndex, bulletIndex) => {
    const updatedFields = { ...fields };

    // For bullets, we'll use an array of objects instead of strings
    if (typeof updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] === 'string') {
      // Convert the string to an object with text and omitted properties
      const bulletText = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex];
      updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        text: bulletText,
        omitted: true // Start as omitted since we're toggling
      };
    } else {
      // Toggle the omitted property
      updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        ...updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex],
        omitted: !updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex].omitted
      };
    }

    setFields(updatedFields);
    //handleSave(updatedFields, false);
  };

  // Function to delete a bullet
  const handleDeleteBullet = (companyIndex, roleIndex, bulletIndex) => {
    if (window.confirm("Are you sure you want to delete this responsibility?")) {
      const updatedFields = { ...fields };
      updatedFields.companies[companyIndex].roles[roleIndex].bullets.splice(bulletIndex, 1);

      setFields(updatedFields);

    }
  };

  // Function to update bullet text
  const handleBulletChange = (companyIndex, roleIndex, bulletIndex, value) => {
    const updatedFields = { ...fields };
    const bullet = updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex];

    if (typeof bullet === 'string') {
      updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = value;
    } else {
      updatedFields.companies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        ...bullet,
        text: value
      };
    }

    setFields(updatedFields);
  };

  // Function to update company field
  const handleCompanyChange = (companyIndex, field, value) => {
    const updatedFields = { ...fields };
    updatedFields.companies[companyIndex][field] = value;
    setFields(updatedFields);
  };

  // Function to update role field
  const handleRoleChange = (companyIndex, roleIndex, field, value) => {
    const updatedFields = { ...fields };
    updatedFields.companies[companyIndex].roles[roleIndex][field] = value;
    setFields(updatedFields);
  };
  const {
    resumeData,
    pdfData: initialPdfData,
    filename,
    metrics,
    similarJobTitles,
    topSkills,
    betaMessage,
    jobTitle,
    userEmail,
    emailSendToken,
  } = location.state || {};

  useEffect(() => {
    // Don't use filter at all - it's causing issues with the portal
    if (showPopup) {
      // Just prevent scrolling
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showPopup]);


  useEffect(() => {
    const initializePage = async () => {
      if (!resumeData && !initialPdfData) {
        navigate('/');
        return;
      }

      if (initialPdfData) {
        setPdfData(initialPdfData);
        setPdfFilename(filename || 'formatted_resume.pdf');
      }

      if (resumeData) {
        setFields(resumeData);
        // Generate PDF from resume data - await the result
        await handleSave(resumeData, false);
      }
    };

    initializePage();
  }, [resumeData, initialPdfData, filename, navigate]);
  // Add this function to filter out omitted items before generating PDF
  const prepareDataForPDF = (data) => {
    if (!data) return data;

    const filteredData = { ...data };
    // Filter out omitted education
    if (filteredData.education) {
      filteredData.education = filteredData.education.filter(edu => !edu.omitted);
    }
    // Filter out omitted companies
    if (filteredData.companies) {
      filteredData.companies = filteredData.companies
        .filter(company => !company.omitted)
        .map(company => ({
          ...company,
          roles: company.roles
            ? company.roles
              .filter(role => !role.omitted)
              .map(role => ({
                ...role,
                bullets: role.bullets
                  ? role.bullets
                    .map(bullet => {
                      if (typeof bullet === 'string') {
                        return bullet;
                      } else if (bullet && !bullet.omitted) {
                        return bullet.text || '';
                      }
                      return '';
                    })
                    .filter(bullet => bullet.trim() !== '')
                  : []
              }))
            : []
        }));
    }

    // Filter out omitted projects
    if (filteredData.projects) {
      filteredData.projects = filteredData.projects.filter(project => !project.omitted);
    }

    return filteredData;
  };
  // Add this line at the top level of your component, outside of any functions
  const isInitialRender = React.useRef(true);

  // Then modify your useEffect to use it like this:
  useEffect(() => {
    // Check if this is the initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // Only regenerate PDF when fields changes and it's not the initial load
    if (fields && pdfData) {
      // Use a debounced version to avoid too many regenerations
      const timeoutId = setTimeout(() => {
        // Important: Filter out omitted items before generating PDF
        const preparedData = prepareDataForPDF(fields);
        generatePDF(preparedData, setPdfData);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [fields]);
  // Function to send the resume via email
  async function sendResumeEmail(email, emailSendToken, userName, resumeTitle, pdfBlob) {
    try {
      await csrfService.getToken(true);
      
      const base64PDF = await blobToBase64(pdfBlob);
  
      const response = await csrfService.fetch(`${stripeServerUrl}/send-resume-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          emailSendToken: emailSendToken,
          userName: userName,
          resumeTitle: resumeTitle,
          pdfContent: base64PDF,
        })
      });
  
      const result = await response.json();
  
      if (!response.ok) {
        throw new Error(result?.message || `Email send failed (${response.status})`);
      }
  
      return result;
    } catch (error) {
      console.error('Error sending resume email:', error);
      throw error;
    }
  }
  // Helper function to convert Blob to base64
  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove the "data:application/pdf;base64," part
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const handleDownloadPdf = async () => {
    if (!pdfData) return;

    const byteCharacters = atob(pdfData);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    // Create filename in the format: {FirstName LastName} - {Job Title} Resume
    const firstName = fields?.first_name || '';
    const lastName = fields?.last_name || '';
    const jobtitle = jobTitle || '';
    const fileName = `${firstName} ${lastName} - ${jobtitle} Resume.pdf`.trim();

    // Create download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    console.log("USER EMAIL", userEmail);
    // Only attempt to send email if we have an email address
    if (userEmail && emailSendToken) {
      try {
        // You could add a loading state here if desired
        // setEmailSending(true);

        await sendResumeEmail(
          userEmail,
          emailSendToken,
          `${fields.first_name || ''} ${fields.last_name || ''}`.trim(),
          fileName,
          blob
        );

        // Show success notification if desired
        //alert("Resume sent to your email successfully!");
      } catch (error) {
        console.error("Failed to send resume email:", error);
        alert("Your resume could not be sent to your email. Please try again.");
      } finally {
        // Reset loading state if needed
        // setEmailSending(false);
      }
    } else if (userEmail && !emailSendToken) {
      console.error('Missing emailSendToken — check SkillsSelection skip path or ProcessingPage navigation');
      alert("Your resume could not be sent to your email. Please try again. If the problem persists, the server may have timed out.");
    }
  };


  const handleSave = async (resumeData = null, closeEditMode = true) => {
    try {
      const dataToUse = resumeData || fields;
      // Filter out omitted items before generating PDF
      const preparedData = prepareDataForPDF(dataToUse);
      const success = await generatePDF(preparedData, setPdfData);

      if (success) {
        if (resumeData) {
          setFields(resumeData);
        }

        if (closeEditMode) {
          setIsEditing(false);
        }
      }
    } catch (error) {
      console.error("Error in handleSave:", error);
    }
  };

  if (!fields && !pdfData) {
    return (
      <div className="homepage">
        <h1 className="centered-title">Loading...</h1>
      </div>
    );
  }

  // Complete return statement with styling for ALL edit sections

  return (
    <div className="results-page">
      <h1 className="centered-title">Your Optimized Resume!</h1>

      {betaMessage && (
        <div className="beta-message">
          <p className="beta-message-text">{betaMessage}</p>
        </div>
      )}

      {pdfData && (
        <>
          {isEditing ? (
            <div className="soft-skills-container">
              <div className="pdf-viewer-container">
                <div className="pdf-viewer-button-container">
                  <button
                    className="download-button"
                    onClick={() => { window.plausible('Download & Email Click', { props: { location: 'edit_mode' } }); handleDownloadPdf(); }}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#15803D',
                      color: 'white',
                      fontSize: '16px',
                      cursor: 'pointer',
                      minWidth: '120px'
                    }}
                  >
                    Download & Email
                  </button>
                  <button
                    className="view-button"
                    onClick={() => { window.plausible('Exit Edit Mode Click'); setIsEditing(false); }}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      backgroundColor: 'white',
                      color: '#333',
                      fontSize: '16px',
                      cursor: 'pointer',
                      minWidth: '120px'
                    }}
                  >
                    Exit Edit Mode
                  </button>
                </div>
                <PDFViewer
                  pdfData={pdfData}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                />

              </div>

              {/* Edit Form Container - Styled to match the image */}
              <div className="edit-form-container" >

                <div className="edit-form-content">
                  <ResumeEditForm
                    fields={fields}
                    onChange={setFields}
                    onSave={handleSave}
                    initialExpandedSection="experience"
                    // Pass the handler functions as props
                    onToggleCompany={handleToggleCompany}
                    onDeleteCompany={handleDeleteCompany}
                    onMoveCompanyUp={handleMoveCompanyUp}
                    onMoveCompanyDown={handleMoveCompanyDown}
                    onAddCompany={handleAddCompany}
                    onAddRole={handleAddRole}
                    onToggleRole={handleToggleRole}
                    onDeleteRole={handleDeleteRole}
                    onMoveRoleUp={handleMoveRoleUp}
                    onMoveRoleDown={handleMoveRoleDown}
                    onAddBullet={handleAddBullet}
                    onToggleBullet={handleToggleBullet}
                    onDeleteBullet={handleDeleteBullet}
                    onCompanyChange={handleCompanyChange}
                    onRoleChange={handleRoleChange}
                    onBulletChange={handleBulletChange}
                    onToggleEducation={handleToggleEducation}
                    onDeleteEducation={handleDeleteEducation}
                    onMoveBulletUp={handleMoveBulletUp}
                    onMoveBulletDown={handleMoveBulletDown}
                    expandedCompanies={expandedCompanies}
                    setExpandedCompanies={setExpandedCompanies}
                    expandedRoles={expandedRoles}
                    setExpandedRoles={setExpandedRoles}
                  />
                </div>
                <div className="edit-form-button-container">
                  <button
                    className="reset-form-button"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to reset all changes?')) {
                        const locationState = location.state || {};
                        if (locationState.resumeData) {
                          // Create a deep copy of the original data
                          const resetData = JSON.parse(JSON.stringify(locationState.resumeData));

                          // Clear all omitted flags in companies and their roles
                          if (resetData.companies) {
                            resetData.companies.forEach(company => {
                              delete company.omitted;
                              if (company.roles) {
                                company.roles.forEach(role => {
                                  delete role.omitted;
                                  if (role.bullets) {
                                    role.bullets.forEach((bullet, index) => {
                                      if (typeof bullet === 'object' && bullet !== null) {
                                        role.bullets[index] = bullet.text || '';
                                      }
                                    });
                                  }
                                });
                              }
                            });
                          }

                          // Clear all omitted flags in education
                          if (resetData.education) {
                            resetData.education.forEach(edu => {
                              delete edu.omitted;
                            });
                          }

                          // Clear all omitted flags in projects
                          if (resetData.projects) {
                            resetData.projects.forEach(project => {
                              delete project.omitted;
                            });
                          }

                          // Reset the fields with cleaned data
                          setFields(resetData);

                          // Reset expanded states
                          setExpandedCompanies({});
                          setExpandedRoles({});
                          setExpandedProjects({});

                          // Regenerate the PDF
                          handleSave(resetData, false);
                        }
                      }
                    }}
                  >
                    Reset
                  </button>
                  <button
                    style={{
                      backgroundColor: '#14532D',
                      color: 'white',
                      border: 'none',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onClick={() => { window.plausible('Done Click'); setIsEditing(false); }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="download-email-button-container">
                <button
                  onClick={() => { window.plausible('Download & Email Click', { props: { location: 'view_mode' } }); handleDownloadPdf(); }}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: '#15803D',
                    color: 'white',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Download & Email
                </button>
                <button
                  onClick={() => { window.plausible('Edit Click'); setIsEditing(true); }}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: 'white',
                    color: '#333',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Edit
                </button>
              </div>
              <PDFViewer
                pdfData={pdfData}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
              />
            </>
          )}
        </>
      )}

      {/* Add a blur overlay when popup is shown */}
      {showPopup && (
        <div className="blur-overlay" />
      )}

      {/* Keep only this popup implementation */}
      {showPopup && (
        <PopupPortal>
          <div className="popup-container">
            <div className="popup-content">
              {/* Close button */}
              <button
                onClick={() => setShowPopup(false)}
                className="popup-close-button"
              >
                ✕
              </button>

              {/* Document icon with checkmark and sparkles */}
              <div className="popup-document-icon">
                {/* Document */}
                <div className="popup-document-icon-content">
                  {/* Document lines */}
                  <div className="popup-document-icon-line"></div>
                  <div className="popup-document-icon-line"></div>
                  <div className="popup-document-icon-line"></div>

                  {/* Checkmark */}
                  <div className="popup-document-icon-checkmark">
                    ✓
                  </div>
                </div>

                {/* Top-left sparkle */}
                <div className="popup-document-icon-sparkle-left">
                  ✦
                </div>

                {/* Bottom-right sparkle */}
                <div className="popup-document-icon-sparkle-right">
                  ✦
                </div>
              </div>

              <h2
                className="popup-document-icon-title">
                Resume Improvements
              </h2>

              {/* Improvement items */}
              <div
                className="popup-document-icon-improvements"
              >
                <div
                  className="popup-document-icon-improvement-item"
                >
                  <span
                    className="popup-document-icon-improvement-item-checkmark"
                  >✓</span>
                  <span
                    className="popup-document-icon-improvement-item-text"
                  >Reading time improved ({metrics.reading_time &&
                    metrics.reading_time.improvement_percentage &&
                    metrics.reading_time.improvement_percentage.percentage
                    ? metrics.reading_time.improvement_percentage.percentage
                    : 0}%)</span>
                </div>

                <div className="popup-document-icon-improvement-item">
                  <span className="popup-document-icon-improvement-item-checkmark">✓</span>
                  <span className="popup-document-icon-improvement-item-text">Buzzwords replaced with impactful statements</span>
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span className="popup-document-icon-improvement-item-checkmark">✓</span>
                  <span className="popup-document-icon-improvement-item-text">Resume optimized to meet ATS requirements</span>
                </div>
              </div>

              <p className="popup-document-icon-improvement-item-paragraph">
                Your resume is now optimized for maximum impact!
              </p>

              <button
                className="popup-document-icon-improvement-item-button"
                onClick={() => setShowPopup(false)}
              >
                View Resume
              </button>
            </div>
          </div>
        </PopupPortal>
      )}
    </div>
  );
};

export default ResultsPage;

// ## Summary

// The new workflow is now:

// 1. **Home Page (`/`)** - User fills out the form and submits
// 2. **Payment Page (`/payment`)** - User pays (or skips if beta testing)
// 3. **Processing Page (`/processing`)** - Shows AI loading animation while backend processes
// 4. **Skills Selection Page (`/skills-selection`)** - Optional step if skills are available to add
// 5. **Results Page (`/results`)** - Final resume with edit options and download

// **To implement:**

// 1. Run `npm install react-router-dom` to install the routing package
// 2. Create a new `src/pages/` directory
// 3. Create the page files as shown above
// 4. Update `src/App.js` with the router configuration
// 5. The original `HomePage.js` in components can be kept as a backup or removed

// Each page is completely separate and navigation happens through React Router's `useNavigate` hook, passing necessary data through `location.state`.